import { Component, Element, h, Prop } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';
/**
 * COMPNAME is for creating a container for an Image of a Journal, Date of the Issue, and up to 3 topics with descriptions and/or links to the next and previous articles within an issue. If "Is Current" is deactivated, it converts to Past Issue Mode, relocating the IssueDate and hiding Topic Descriptions. On Mobile, all components are hidden except for the centered Image.
 *
 * <em>For More Information:</em>
 * AEM Component- aafp-aem-library.git/ui.apps/src/main/content/jcr_root/apps/brand/aafp/components/content/aafp-journal-preview
 * Sling Model- core.src.main.java.org.aafp.aem.core.models.AafpJournalPreviewModel.java
 */
@Component({
  tag: 'aafp-journal-preview',
  shadow: false,
})
export class AafpJournalPreview {
  /**
   * Stores the PROPNAME asset. No Datepicker.
   */
  @Prop() journalIssueDate!: string;
  /**
   * Stores the PROPNAME asset.
   * True: Current Mode
   * False: Past mode
   */
  @Prop() journalIsCurrent: boolean;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalCtaLink: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalCtaText: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalCtaAriaLabel: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalCtaIsExternalLink: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalTopicOneTitle: string;
  /**
   * Stores the PROPNAME asset. 3 Line Limit.
   */
  @Prop() journalTopicOneText: string;
  /**
   * Link to first topic page
   */
  @Prop() journalTopicOneLink: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalTopicTwoTitle: string;
  /**
   * Stores the PROPNAME asset. 3 Line Limit.
   */
  @Prop() journalTopicTwoText: string;
  /**
   * Link to second topic page
   */
  @Prop() journalTopicTwoLink: string;
  /**
   * Stores the PROPNAME asset
   */
  @Prop() journalTopicThreeTitle: string;
  /**
   * Stores the PROPNAME asset. 3 Line Limit.
   */
  @Prop() journalTopicThreeText: string;
  /**
   * Link to third topic page
   */
  @Prop() journalTopicThreeLink: string;

  @Prop() journalPreviewTitle: string;

  /**
   * Text for the link to the previous article
   */
  @Prop() previousArticleTitle: string;

  /**
   * If true, the width of the column with the image on desktop will be based on the width of the image (up to a maximum of 50% of the container)
   */
  @Prop() flexibleImageWidth: boolean;

  /**
   * Link to previous article
   */
  @Prop() previousArticleLink: string;

  /**
   * Text for the link to the next article
   */
  @Prop() nextArticleTitle: string;

  /**
   * Link to next article
   */
  @Prop() nextArticleLink: string;

  @Element() element: HTMLElement;

  renderTopicTitle(title: string, link: string) {
    if (!title) {
      return null;
    }

    const heading = (
      <h3
        class={getClassListString({
          'adl-journal-preview__topic-title-current-issue adl-link adl-h3': this.journalIsCurrent,
          'adl-journal-preview__topic-title-past-issue adl-link adl-body': !this.journalIsCurrent,
        })}
      >
        {title}
      </h3>
    );

    return link ? (
      <a href={link} aria-label={title}>
        {heading}
      </a>
    ) : (
      heading
    );
  }

  render() {
    return (
      <div
        class={getClassListString(
          { 'adl-journal-preview__wrapper--flex-image': this.flexibleImageWidth },
          'adl-journal-preview__wrapper',
        )}
      >
        {this.journalIsCurrent && this.journalIssueDate && (
          <h4 class="adl-journal-preview__date adl-subtitle2" innerHTML={this.journalIssueDate}></h4>
        )}
        <div class="adl-journal-preview__issue-data">
          <div class="adl-journal-preview__img-wrapper">
            <div class="adl-journal-preview__mobile">
              <slot name="journal-image-mobile"></slot>
              {this.journalPreviewTitle && (
                <div class="adl-journal-preview__title adl-text-align--center adl-h3">{this.journalPreviewTitle}</div>
              )}
            </div>
            <div class="adl-journal-preview__desktop">
              <slot name="journal-image-desktop"></slot>
            </div>
          </div>
        </div>
        <div class="adl-journal-preview__topic-data">
          {!this.journalIsCurrent && this.journalIssueDate && (
            <h2 class="adl-journal-preview__date adl-subtitle2" innerHTML={this.journalIssueDate}></h2>
          )}
          {this.renderTopicTitle(this.journalTopicOneTitle, this.journalTopicOneLink)}
          {this.journalIsCurrent && this.journalTopicOneText && (
            <p class="adl-body adl-journal-preview__topic-text">{this.journalTopicOneText}</p>
          )}
          {this.renderTopicTitle(this.journalTopicTwoTitle, this.journalTopicTwoLink)}
          {this.journalIsCurrent && this.journalTopicTwoText && (
            <p class="adl-body adl-journal-preview__topic-text">{this.journalTopicTwoText}</p>
          )}
          {this.renderTopicTitle(this.journalTopicThreeTitle, this.journalTopicThreeLink)}
          {this.journalIsCurrent && (
            <p class="adl-body adl-journal-preview__topic-text">{this.journalTopicThreeText}</p>
          )}
          {this.previousArticleLink && (
            <p class="adl-body adl-journal-preview__article-link">
              Previous:{' '}
              <a class="adl-link" href={this.previousArticleLink}>
                {this.previousArticleTitle}
              </a>
            </p>
          )}
          {this.nextArticleLink && (
            <p class="adl-body adl-journal-preview__article-link">
              Next:{' '}
              <a class="adl-link" href={this.nextArticleLink}>
                {this.nextArticleTitle}
              </a>
            </p>
          )}
          <div class="adl-journal-preview__cta-wrapper">
            <slot name="cta-button"></slot>
          </div>
        </div>
      </div>
    );
  }
}
